import React, { useRef, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import StickyScrollTable from '../../../../../components/common/StickyScrollTable';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { dateOnlyBodyTemplate } from '../../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../../utils/textUtils';
import { useNavigate } from 'react-router-dom';

const PreApprovalTable = ({ preApprovals, loading, filters, handleEdit, handleDelete, showHistoryDialog, tabLabel, onOpenCustomerStatement }) => {
    const { th } = useLanguage();
    const dataTableRef = useRef(null);
    const navigate = useNavigate();

    const claimNoBodyTemplate = (rowData) => {
        if (!rowData.claims || rowData.claims.length === 0) return '-';

        return (
            <div className="flex flex-wrap gap-1">
                {rowData.claims.map((claim, index) => (
                    <React.Fragment key={claim.claimNo}>
                        <span className="text-blue-500 cursor-pointer hover:text-blue-700 hover:underline" onClick={() => navigate(`/warranty-management/warranty-requests/claims/${claim.claimId}`)}>
                            {claim.claimNo}
                        </span>
                        {index < rowData.claims.length - 1 && <span>, </span>}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const customerStatementBodyTemplate = (rowData) => {
        if (!rowData.claims || rowData.claims.length === 0) return '-';

        return (
            <div className="flex flex-wrap gap-1">
                {rowData.claims.map((claim, index) => (
                    <React.Fragment key={claim.customerStatementNo}>
                        <span className="text-blue-500 cursor-pointer hover:text-blue-700 hover:underline" onClick={() => onOpenCustomerStatement && onOpenCustomerStatement(claim.customerStatementNo)}>
                            {claim.customerStatementNo}
                        </span>
                        {index < rowData.claims.length - 1 && <span>, </span>}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const confirmDelete = (id) => {
        confirmDialog({
            message: th('preApproval.messages.deleteConfirm') || 'Bu kaydı silmek istediğinizden emin misiniz?',
            header: th('preApproval.messages.deleteConfirmHeader') || 'Silme Onayı',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: th('common.yes') || 'Evet',
            rejectLabel: th('common.no') || 'Hayır',
            accept: () => handleDelete(id)
        });
    };

    const actionBodyTemplate = (rowData) => {
        const isDraft = rowData.status === 10;
        return (
            <div className="flex gap-2 justify-content-center">
                <i onClick={() => handleEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('brand.editTooltip')}></i>
                <i onClick={() => showHistoryDialog(rowData)} className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')}></i>
                {isDraft && <i onClick={() => confirmDelete(rowData.id)} className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700" style={{ fontSize: '1.1rem' }} title={th('brand.deleteTooltip')}></i>}
            </div>
        );
    };

    return (
        <StickyScrollTable deps={[preApprovals]}>
            <DataTable
                value={preApprovals}
                paginator
                className="p-datatable-gridlines"
                showGridlines
                rows={10}
                dataKey="id"
                loading={loading}
                filters={filters}
                filterDisplay="menu"
                globalFilterFields={['preApprovalNo', 'chassisNo', 'dealerCode', 'dealerName', 'countryCode', 'plateNo']}
                emptyMessage={th('preApproval.table.noRecordsFound')}
                rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
                ref={dataTableRef}
                pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
            >
                <Column body={actionBodyTemplate} exportable={false} style={{ width: '80px', minWidth: '80px' }} />
                <Column sortable field="dealerCode" header={th('preApproval.table.dealerCode') || 'Bayi Kodu'} />
                <Column sortable field="dealerName" header={th('preApproval.table.dealerName')} body={(rowData) => createTruncatedTextForTable(rowData.dealerName, 40)} />
                <Column sortable field="countryCode" header={th('preApproval.table.country') || 'Ülke'} />
                <Column sortable field="preApprovalNo" header={th('preApproval.table.preApprovalNo')} />
                <Column sortable field="chassisNo" header={th('preApproval.table.chassisNo')} />
                <Column sortable field="repairDate" header={th('preApproval.table.repairDate')} body={(rowData) => dateOnlyBodyTemplate(rowData, 'repairDate')} />
                <Column header={th('preApproval.table.requestNo')} body={claimNoBodyTemplate} style={{ minWidth: '120px' }} />
                <Column header={th('preApproval.table.workOrder')} body={customerStatementBodyTemplate} style={{ minWidth: '140px' }} />
            </DataTable>
        </StickyScrollTable>
    );
};

export default PreApprovalTable;
